import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { Grid as InavistaGrid, GridConfig, GridHyperlinkClickEvent } from '@inavista/components/grid';
import { InventoryService } from 'projects/service/src/app/modules/home/services/inventory.service';
import { ApiResponse, LinksEnum } from '../../models/app.model';
import { CommonService } from '../../services/common.service';
import { exportInavistaGrid } from '../../utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from '../../utils/local-rows-grid-api';
import { statusPillColor } from '../../utils/status-pill-colors';
import { buildLinksTabGridConfig, LINKS_COLUMNS } from './links-tab.grid.config';

// The old grid showed up to 20 rows (paged 20). No paging now (Martin): the grid is as tall as its rows, up to 20,
// then scrolls. Toolbar + header + horizontal scrollbar, and the row height of the config.
const GRID_CHROME_PX = 92;
const ROW_PX = 30;
const MAX_VISIBLE_ROWS = 20;
const MIN_VISIBLE_ROWS = 2;

@Component({
    selector: 'app-links-tab-shared',
    templateUrl: './links-tab.component.html',
    styleUrls: ['./links-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    // The grid reads this.links, not an endpoint.
    providers: [...provideLocalGridRows()]
})
export class LinksTabSharedComponent implements OnInit, OnChanges {

    id: any;

    showLoader: boolean;

    @ViewChild('linksTabGrid') linksTabGrid: InavistaGrid;

    @Input() serviceOrderId: string = '';
    @Input() links;
    @Output() onLinkIdClick: EventEmitter<any> = new EventEmitter();
    showPartsOrderPopup: boolean = false;

    partsOrderId: string = "";
    partsOrderData: object = {};
    itemsData: any = [];

    gridConfig: GridConfig = null;
    gridHeight = GRID_CHROME_PX + MIN_VISIBLE_ROWS * ROW_PX;
    private readonly rows = inject(LocalRowsGridApi);

    constructor(
        private commonService: CommonService,
        private inventoryService: InventoryService
    ) { }

    ngOnInit(): void {
        this.rows.columns.set(LocalRowsGridApi.schema(LINKS_COLUMNS));
        this.gridConfig = buildLinksTabGridConfig(
            !!this.commonService.roleClaims?.['AllGrid_Toolbar_Excel_Export']?.visible,
            statusPillColor(s => this.commonService.getStatusClass(s)));
        this.refreshRows();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['links'] && !changes['links'].firstChange) {
            this.refreshRows();
        }
    }

    // The rows the grid shows: this.links as they came (GET Links/getLinkedRecords).
    refreshRows() {
        const links = Array.isArray(this.links) ? this.links : [];
        this.rows.rows.set(links.map(link => ({ ...link })));
        this.gridHeight = GRID_CHROME_PX + Math.min(Math.max(links.length, MIN_VISIBLE_ROWS), MAX_VISIBLE_ROWS) * ROW_PX;
        this.linksTabGrid?.refresh();
    }

    // Order ID and Customer emit the link, as before.
    onHyperlinkClick(event: GridHyperlinkClickEvent) {
        const link = (this.links ?? []).find(l => l.srNo === event.row['srNo']) ?? event.row;
        this.onLinkIdClick.emit(link);
    }

    // Syncfusion's excelExport(): the rows the grid shows, the visible columns.
    onExportRequested() {
        exportInavistaGrid(this.linksTabGrid, 'Links');
    }

    openPartsOrder(data) {
        this.partsOrderId = data.orderId;
        this.inventoryService.getPartsOrderById(this.partsOrderId)
            .subscribe((res: ApiResponse) => {
                this.partsOrderData = res.result;
                this.itemsData = res.result.itemList;
                this.showPartsOrderPopup = true;
            });
    }

    partsOrderPopupClose() {
        this.showPartsOrderPopup = false;
        this.partsOrderData = null
    }

    partsOrderClose() {
        this.showLoader = true;
        this.showPartsOrderPopup = false;
        this.partsOrderData = null
        this.commonService.getLinkedRecords(LinksEnum.serviceOrder, this.serviceOrderId)
            .subscribe((res: ApiResponse) => {
                if (res.isSuccessful) {
                    this.links = res.result;
                    this.refreshRows();
                    this.commonService.showNotification('success', res.message);
                } else {
                    this.commonService.showNotification('error', res.message);
                }
                this.showLoader = false;
            }, (error: HttpErrorResponse) => {
                this.showLoader = false;
                this.commonService.showNotification('error', error.message);
            });
    }
}
